import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { apiGet } from "@/lib/api";
import type { PostDto } from "@/types";
import { RichText } from "@/components/content/RichText";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import { ResponsiveImage } from "@/components/content/ResponsiveImage";

export function PostPage() {
  const { slug = "" } = useParams();
  const locale = de;

  const { data: post, isLoading, error } = useQuery({
    queryKey: ["post", slug, "de"],
    queryFn: () => apiGet<PostDto>("/public/posts/" + slug + "?locale=" + "de"),
    retry: false,
  });

  if (isLoading) return <div className="animate-pulse text-text-muted">...</div>;
  if (error || !post) {
    return (
      <div>
        <h1 className="text-2xl font-bold">{"Diese Seite wurde leider nicht gefunden."}</h1>
        <Link to="/aktuelles" className="text-primary hover:underline">{"Aktuelles"}</Link>
      </div>
    );
  }

  return (
    <article>
      <Helmet>
        <title>{post.title} | Falkenrehde</title>
        {post.excerpt && <meta name="description" content={post.excerpt} />}
        <meta property="og:title" content={post.title} />
        {post.excerpt && <meta property="og:description" content={post.excerpt} />}
        {post.coverImage && <meta property="og:image" content={post.coverImage.urlLg} />}
        <meta property="og:type" content="article" />
      </Helmet>
      <Breadcrumb items={[{ label: "Aktuelles", to: "/aktuelles" }, { label: post.title }]} />

      <header className="mb-6">
        <div className="text-sm text-text-muted mb-2 flex items-center gap-2 flex-wrap">
          {post.publishedAt && <time>{format(parseISO(post.publishedAt), "d. MMMM yyyy", { locale })}</time>}
          {post.author?.displayName && <span>{"von"} {post.author.displayName}</span>}
          {post.category && <span className="bg-primary/10 text-primary px-2 py-0.5 rounded text-xs uppercase">{post.category}</span>}
        </div>
        <h1 className="text-3xl lg:text-4xl font-bold leading-tight">{post.title}</h1>
        {post.excerpt && <p className="text-lg text-text-muted mt-3">{post.excerpt}</p>}
      </header>

      {post.coverImage && (
        <figure className="mb-8">
          <ResponsiveImage media={post.coverImage} sizes="(min-width: 1024px) 720px, 100vw" className="w-full rounded-lg" loading="eager" />
          {post.coverImage.alt && (
            <figcaption className="text-xs text-text-muted mt-2">{post.coverImage.alt}</figcaption>
          )}
        </figure>
      )}

      <RichText html={post.bodyHtml} />

      {post.tags && post.tags.length > 0 && (
        <div className="mt-10 pt-6 border-t border-border flex flex-wrap gap-2">
          {post.tags.map((tag) => (
            <span key={tag} className="text-xs px-2 py-1 rounded-md border border-border text-text-muted bg-bg-soft">#{tag}</span>
          ))}
        </div>
      )}

      <ShareBar title={post.title} />
    </article>
  );
}

function ShareBar({ title }: { title: string }) {
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  return (
    <div className="mt-6 flex items-center gap-3 text-sm text-text-muted">
      <span>Teilen:</span>
      <a className="hover:text-primary" target="_blank" rel="noopener noreferrer"
        href={"mailto:?subject=" + encodeURIComponent(title) + "&body=" + encodeURIComponent(shareUrl)}>E-Mail</a>
      <a className="hover:text-primary" target="_blank" rel="noopener noreferrer"
        href={"https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(shareUrl)}>Facebook</a>
      <a className="hover:text-primary" target="_blank" rel="noopener noreferrer"
        href={"https://twitter.com/intent/tweet?url=" + encodeURIComponent(shareUrl) + "&text=" + encodeURIComponent(title)}>X / Twitter</a>
      <button type="button" className="hover:text-primary"
        onClick={() => navigator.clipboard?.writeText(shareUrl)}>Link kopieren</button>
    </div>
  );
}
